import React, { useState, useRef, useEffect } from 'react';
import { BasicTaskList } from '../../styles/BasicTaskList';
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Task, TaskItem } from '../../../taskList/interfaces';
import StageSection from '../StageSection';
import { CandidateMessages } from '../../../common/utils/getCandidateMessages';
import { useTranslation } from 'react-i18next';
import CkEditorContent from '../../../common/components/CkEditorContent';
import { useEndWorkflow } from './../../../taskList/hooks/useEndWorkflow';
import { FullPageLoader } from '../../../common/components';
import { SessionStore } from '../../../constants/sessionStore';
import { ResponseReuseType } from '../../../taskList/constants';
import RRMessagePopup from '../RRMessagePopup';
import { useAppSelector } from '../../../store/hook';

export interface RRMessageProps {
  title: string;
  subtitle1: string;
  subtitle2: string;
  subtitle3: string;
  buttonText: string;
  onRRMsgBtnClick?: (showPopup: boolean, currentItem?: TaskItem) => void;
  isRRApplied?: boolean;
  showRRPopup?: boolean;
}
interface BasicTaskListLayoutProps {
  data: Task[];
  candidateMessages: CandidateMessages;
  deadlineDate: string | undefined;
  isRRApplied: boolean;
  rrPopupInfo: RRMessageProps;
}

const BasicTaskListLayout = (props: BasicTaskListLayoutProps): React.JSX.Element => {
  const { data, candidateMessages, deadlineDate, isRRApplied } = props;
  const { settings } = useAppSelector((state) => state.template);
  const { t } = useTranslation();
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const { classes } = BasicTaskList({ isICPEnabled, settings });
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const rrMsgRead = sessionStorage.getItem(SessionStore.basicRRMsgRead) !== 'true';
  const [showRRPopup, setShowRRPopup] = useState<boolean>(isRRApplied && rrMsgRead);
  const endLoaderStatus: boolean = useEndWorkflow(data);
  const [
    rrPopupInfo = { title: '', buttonText: '', subtitle1: '', subtitle2: '', subtitle3: '', subtitle4: '' },
    setRrPopupInfo,
  ] = useState<RRMessageProps>(props.rrPopupInfo);
  const basicTasklistRef = useRef<HTMLDivElement>(null);

  const isDeadlineAvail = deadlineDate && deadlineDate !== 'Invalid date' && deadlineDate.length > 0;

  const onRRMsgBtnClick = (showPopup: boolean, currentItem?: TaskItem): void => {
    if (rrMsgRead) sessionStorage.setItem(SessionStore.basicRRMsgRead, 'true');

    if (currentItem) {
      const rrMessageInfo = { ...rrPopupInfo };
      rrMessageInfo.buttonText = t('gotIt');
      if (currentItem?.responseReuseType === ResponseReuseType.PARTIAL_RR)
        rrMessageInfo.subtitle1 = t('prrPopupSubTitle4');
      else rrMessageInfo.subtitle1 = t('prrPopupSubTitle5');
      setRrPopupInfo(rrMessageInfo);
    }

    setShowRRPopup(showPopup);

    if (showPopup) {
      basicTasklistRef.current?.setAttribute('inert', '');
    } else {
      basicTasklistRef.current?.removeAttribute('inert');
    }
  };
  const documentTitle = document.title?.split('|')[0];

  if (showRRPopup) {
    basicTasklistRef.current?.setAttribute('inert', '');
  }
  useEffect(() => {
    setShowRRPopup(isRRApplied && rrMsgRead);
    setRrPopupInfo(props.rrPopupInfo);
  }, [isRRApplied, rrMsgRead, props.rrPopupInfo]);
  return (
    <>
      {endLoaderStatus ? (
        <FullPageLoader isLoading />
      ) : (
        <>
          <RRMessagePopup
            title={rrPopupInfo.title}
            subtitle1={rrPopupInfo.subtitle1}
            subtitle2={rrPopupInfo.subtitle2}
            subtitle3={rrPopupInfo.subtitle3}
            buttonText={rrPopupInfo.buttonText}
            isRRApplied={isRRApplied}
            onRRMsgBtnClick={onRRMsgBtnClick}
            showRRPopup={showRRPopup}
          />
          <Grid ref={basicTasklistRef} container justifyContent="center" className={classes.container}>
            <Grid item lg={10} className={classes.grid}>
              <Paper role="region" aria-label={documentTitle} className={classes.paper}>
                <Grid container justifyContent={!smUp ? 'center' : 'flex-start'} className={classes.taskListHeader}>
                  <Grid item xs={12} sm={isDeadlineAvail ? 7 : 12} className={classes.headerGrid}>
                    <Typography variant="h1">{candidateMessages.taskListPageHeading}</Typography>
                  </Grid>
                  {isDeadlineAvail && (
                    <Grid
                      container
                      justifyContent={smUp ? 'center' : 'flex-end'}
                      item
                      xs={12}
                      sm={5}
                      className={classes.headerGrid}
                      style={{ order: !smUp ? 3 : 0 }}>
                      <Typography variant="caption" className={`${classes.typographyCation}`}>
                        <strong>{t('candidateDeadline')}</strong> {deadlineDate}
                      </Typography>
                    </Grid>
                  )}
                  <Box marginTop={smUp ? 0.5 : 0.6} className={classes.headerDescriptionText}>
                    <CkEditorContent
                      content={candidateMessages.taskListPageContent}
                      pageStartTime={new Date()}
                      generalContentName={'BASICTASKLIST_PAGE'}
                    />
                  </Box>
                </Grid>
                <StageSection data={data} onRRMsgBtnClick={onRRMsgBtnClick} />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default BasicTaskListLayout;
